import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContributionAccountField } from '@/components/giving/contribution-account-field';
import { RecordContributionSaveOptions } from '@/components/giving/record-contribution-save-options';
import { FundSelect } from '@/components/shared/fund-select';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { toast } from '@/components/ui/toast';
import { useGivingSettings } from '@/hooks/use-giving-settings';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import {
  defaultFundPurposeForKind,
  resolveDefaultFund,
} from '@/lib/giving-default-funds';
import {
  loadRecordContributionPreferences,
  persistRecordContributionPreferences,
} from '@/lib/record-contribution-preferences';
import { useChurchStore } from '@/stores/church-store';
import {
  accountKindForMethod,
  CONTRIBUTION_METHODS,
  type ContributionMethod,
} from '@/lib/contribution-methods';

const CONTRIBUTION_KINDS = [
  'tithe',
  'offering',
  'first_fruit',
  'seed',
  'welfare',
  'project',
  'general',
] as const;

const todayIso = () => new Date().toISOString().slice(0, 10);

interface Contribution {
  id: string;
  fundId: string;
  contributionType?: string;
  amountMinor: number;
  currency: string;
  contributionDate: string;
  method: string;
  receiptNumber: number;
  isVoided: boolean;
  identityHidden: boolean;
}

type RecordGivingIntent = 'any' | 'tithe' | 'offering';

interface MemberRecordGivingDialogProps {
  memberId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intent?: RecordGivingIntent;
}

const defaultKindForIntent = (
  intent: RecordGivingIntent,
): (typeof CONTRIBUTION_KINDS)[number] => (intent === 'offering' ? 'offering' : 'tithe');

const dialogTitleKey = {
  any: 'members.detail.giving.record',
  tithe: 'members.detail.giving.recordTithe',
  offering: 'members.detail.giving.recordOffering',
} as const;

/**
 * Record a contribution for a person. Hosted on the member detail page so the
 * action is available from the page header without opening the Giving tab first.
 */
export const MemberRecordGivingDialog = ({
  memberId,
  open,
  onOpenChange,
  intent = 'any',
}: MemberRecordGivingDialogProps) => {
  const { t } = useTranslation('people');
  const { t: tGiving } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const givingSettings = useGivingSettings();

  const [kind, setKind] = useState<(typeof CONTRIBUTION_KINDS)[number]>(
    defaultKindForIntent(intent),
  );
  const [fundId, setFundId] = useState<string | null>(null);
  const [fundCurrency, setFundCurrency] = useState<string | null>(null);
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [contributionDate, setContributionDate] = useState<string | null>(todayIso());
  const [method, setMethod] = useState<ContributionMethod>('cash');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [savePreferences, setSavePreferences] = useState(() =>
    loadRecordContributionPreferences(church?.id),
  );
  const [formKey, setFormKey] = useState(0);

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'active'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; defaultCurrency: string; isActive: boolean }> }>(
        '/giving/funds?pageSize=100&activeOnly=true',
      ),
    enabled: open,
  });

  const currency = fundCurrency ?? church?.baseCurrency ?? 'GHS';

  const applyDefaultFund = (nextKind: (typeof CONTRIBUTION_KINDS)[number] = kind) => {
    const purpose = defaultFundPurposeForKind(nextKind);
    const defaults = resolveDefaultFund(givingSettings.data, purpose, funds.data?.data);
    setFundId(defaults.fundId);
    setFundCurrency(defaults.currency);
  };

  const resetForm = () => {
    const nextKind = defaultKindForIntent(intent);
    setKind(nextKind);
    setAmountMinor(null);
    setContributionDate(todayIso());
    setMethod('cash');
    setAccountId(null);
    applyDefaultFund(nextKind);
  };

  useEffect(() => {
    if (!open) return;
    setKind(defaultKindForIntent(intent));
  }, [open, intent]);

  useEffect(() => {
    if (!open) return;
    applyDefaultFund();
  }, [open, kind, givingSettings.data, funds.data?.data]);

  const record = useMutation({
    mutationFn: () =>
      api.post<Contribution>('/giving/contributions', {
        fundId,
        memberId,
        amountMinor,
        currency,
        contributionDate,
        method,
        financialPositionItemId: accountKindForMethod(method) ? accountId : null,
        contributionType: kind,
      }),
    onSuccess: () => {
      toast.success(t('members.detail.giving.recorded'));
      void queryClient.invalidateQueries({ queryKey: ['giving', 'member-history', memberId] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'contributions'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'tithes'] });
      if (savePreferences.addAnother) {
        resetForm();
        setFormKey((current) => current + 1);
        return;
      }
      onOpenChange(false);
      resetForm();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) resetForm();
      }}
    >
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t(dialogTitleKey[intent])}</DialogTitle>
        </DialogHeader>
        <form
          key={formKey}
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!fundId || amountMinor == null || !contributionDate) return;
            record.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label>{t('members.detail.giving.kindLabel')}</Label>
            <Listbox
              value={kind}
              onChange={(value) => setKind((value as typeof kind) ?? 'tithe')}
              options={CONTRIBUTION_KINDS.map((value) => ({
                value,
                label: tGiving(`kinds.${value}` as 'kinds.tithe'),
              }))}
              popoverPortal="dialog"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t('members.detail.giving.fundLabel')}</Label>
            <Tip tipId="members.giving.fund" />
            <FundSelect
              value={fundId}
              onChange={(next, fund) => {
                setFundId(next);
                setFundCurrency(fund?.defaultCurrency ?? null);
              }}
              popoverPortal="dialog"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{tGiving('form.amount')}</Label>
            <MoneyInput value={amountMinor} onChange={setAmountMinor} currency={currency} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{tGiving('form.date')}</Label>
            <DatePicker
              value={contributionDate}
              onChange={setContributionDate}
              popoverPortal="dialog"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{tGiving('form.method')}</Label>
            <Listbox
              value={method}
              onChange={(value) => {
                setMethod((value as typeof method) ?? 'cash');
                setAccountId(null);
              }}
              options={CONTRIBUTION_METHODS.map((value) => ({
                value,
                label: tGiving(`methods.${value}`),
              }))}
              popoverPortal="dialog"
            />
          </div>
          <ContributionAccountField
            open={open}
            method={method}
            currency={currency}
            value={accountId}
            onChange={setAccountId}
          />
          <RecordContributionSaveOptions
            addAnother={savePreferences.addAnother}
            onAddAnotherChange={(addAnother) => {
              const next = { ...savePreferences, addAnother };
              setSavePreferences(next);
              persistRecordContributionPreferences(church?.id, next);
            }}
            disabled={record.isPending}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button
              type="submit"
              loading={record.isPending}
              disabled={
                !fundId ||
                amountMinor == null ||
                !contributionDate ||
                Boolean(accountKindForMethod(method) && !accountId)
              }
            >
              {t('members.detail.giving.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
